import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
// Import Shared Components
import { Input, Loader } from 'shared-components';
// Import Shared Utils
import { get, post } from 'shared-components/src/utils/http';
import { assignSafeUrl } from 'shared-components/src/utils/safeUrl';
import auth from 'shared-components/src/utils/auth';
// Import Global Layouts
import InputGroup from '../../../../layouts/InputGroup';
// Import Local Layouts
import AuthLayout from '../../layouts/Auth';
import { AuthForm, AuthNonForm } from '../../layouts/InnerAuth';
// Import Global Components
import Text from '../../../../components/Text';
// Import Component Styles
import { FieldError, FormGroup } from '../../styles';
// Import Utils
import { getCurrentAccount } from '../../../../utils/accounts';
import { clearPinSetupPending } from '../../../../utils/pinSetup';
import {
  PIN_LENGTH,
  toPinEntry,
  pinRuleViolation,
} from '../../../../utils/pinRules';

const PAGE = 'register-set-pin';

// A PIN is four digits, and the fields hold nothing else (toPinEntry): anything
// non-numeric is dropped and the entry stops at four. Enforced in state rather
// than with maxLength alone so React state and the field can never disagree.
const isCompletePin = value => value.length === PIN_LENGTH;

// Every endpoint this screen calls is authenticated, and the internal pipeline
// behind the PIN routes answers an expired token with a bare 401. The local
// token can still look valid at that point, so the status is what decides.
const isSessionExpired = e => e && e.status === 401;

// Nothing records whether a cardholder already has a PIN. The only PIN columns
// in card issuing are the two program capability flags, the processor is the
// sole authority, and Galileo will not answer the question at all. Rather than
// probe -- which would reveal a PIN to answer a yes/no, and trip the step-up
// password challenge -- the step is simply always offered and always skippable,
// and the copy tells cardholders who already have one to skip.
const isGalileoProcessor = processor =>
  !!processor && processor.processor_name === 'galileo';

export default () => {
  const intl = useIntl();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [account, setAccount] = useState(null);
  const [processor, setProcessor] = useState(null);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [succeeded, setSucceeded] = useState(false);
  const [commitFailed, setCommitFailed] = useState(false);

  // Leaving this screen always ends the registration PIN step, whether the
  // cardholder set a PIN, skipped, or hit an error they could not clear.
  const leaveFor = path => {
    clearPinSetupPending();
    window.location.pathname = path;
  };

  const finish = () => leaveFor('/');
  const toLogin = () => leaveFor('/login');

  // A set PIN is confirmed on this screen rather than on the dashboard: the
  // hand-off is a full page load, so router state would not survive it.
  const succeed = () => {
    clearPinSetupPending();
    setError(false);
    setSubmitting(false);
    setLoading(false);
    setSucceeded(true);
  };

  useEffect(() => {
    // Someone can reach this URL directly, or come back to it after their
    // session has gone. Every endpoint below is authenticated and would reject
    // the request, so send them to log in rather than bouncing them off the
    // dashboard's guard.
    if (!auth.isAuthenticated()) {
      toLogin();
      return;
    }

    const query = new URLSearchParams(location.search);
    const setPinState = query.get('set_pin_state');

    // Returning from Galileo's hosted form. The new PIN is only staged there, so
    // it takes a commit to land.
    if (setPinState === 'success') {
      const commitPin = async () => {
        try {
          await post(
            `/api/v1/cardholder_portal/cardholder/accounts/${query.get(
              'account_id'
            )}/pin/commit`,
            { last_four_digits: query.get('last_four_digits') }
          );
          succeed();
        } catch (e) {
          if (isSessionExpired(e) || !auth.isAuthenticated()) {
            toLogin();
            return;
          }
          // Terminal: the return leg skips the load below, so there is no
          // account here to retry with, and the staged PIN is gone either way.
          clearPinSetupPending();
          setCommitFailed(true);
          setLoading(false);
        }
      };
      commitPin();
      return;
    }

    if (setPinState === 'failure') {
      setError(`${PAGE}-failed`);
    }

    // Whether there is a step to show at all was settled by registration, which
    // resolved the company opt-in and the program's PIN set flag into
    // `set_pin_offered` and only then marked the step pending. Nothing is
    // re-checked here; these two calls are the data the screen needs to render
    // and submit, not a gate.
    const load = async () => {
      try {
        const [cardholderRes, processorRes] = await Promise.all([
          get('/api/v1/cardholder_portal/cardholder'),
          get('/api/v1/cardholder_portal/cardholder/program_processor'),
        ]);

        const currentAccount = getCurrentAccount(cardholderRes.data);

        // Not a gate on the step, but on this screen being able to work at all:
        // every path below needs an account id and a card to submit against.
        if (
          !currentAccount ||
          !currentAccount.cards ||
          !currentAccount.cards.length
        ) {
          finish();
          return;
        }

        setAccount(currentAccount);
        setProcessor(processorRes.data);
        setLoading(false);
      } catch (e) {
        // These endpoints sit behind two different validators that do not
        // report an unauthenticated request the same way, so the status is
        // checked alongside the local session rather than the error message.
        if (isSessionExpired(e) || !auth.isAuthenticated()) {
          toLogin();
          return;
        }
        // Anything else: the PIN step is optional, so never strand the
        // cardholder here.
        finish();
      }
    };

    load();
    // Runs once: the Galileo return is a fresh page load, not a re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Galileo hands PIN entry to its own hosted form and redirects back here.
  const startGalileoSetPin = async () => {
    setSubmitting(true);
    setError(false);

    try {
      const lastFourDigits = account.cards[0].last_four_digits;
      const base = `${window.location.origin}/register-set-pin`;
      const params = `account_id=${account.id}&last_four_digits=${lastFourDigits}`;

      const {
        data: { url },
      } = await post(
        `/api/v1/cardholder_portal/cardholder/accounts/${account.id}/pin/direct_render`,
        {
          last_four_digits: lastFourDigits,
          // Galileo renders its hosted form in this locale. Without it the page
          // falls back to English regardless of the portal's language.
          locale: intl.locale,
          callback_url: `${base}?set_pin_state=success&${params}`,
          failure_callback_url: `${base}?set_pin_state=failure&${params}`,
        }
      );

      // Only an http(s) URL is followed; anything else is reported like a failed request.
      if (!assignSafeUrl(url)) throw new Error('Unsafe Set PIN URL');
    } catch (e) {
      if (isSessionExpired(e)) {
        toLogin();
        return;
      }
      setError(`${PAGE}-failed`);
      setSubmitting(false);
    }
  };

  const submitPin = async () => {
    // Both fields are checked, not just the first: the entry rule above keeps
    // each one numeric, so all that is left to catch is a short one.
    if (!isCompletePin(pin) || !isCompletePin(confirmPin)) {
      setFieldError(`${PAGE}-invalid`);
      return;
    }

    if (pin !== confirmPin) {
      setFieldError(`${PAGE}-mismatch`);
      return;
    }

    const violation = pinRuleViolation(pin, account.cards[0].last_four_digits);
    if (violation) {
      setFieldError(`${PAGE}-${violation}`);
      return;
    }

    setFieldError('');
    setSubmitting(true);
    setError(false);

    try {
      await post(
        `/api/v1/cardholder_portal/cardholder/accounts/${account.id}/set_pin`,
        {
          last_four_digits: account.cards[0].last_four_digits,
          pin,
        }
      );
      succeed();
    } catch (e) {
      if (isSessionExpired(e)) {
        toLogin();
        return;
      }
      // Anything else is most likely the processor refusing the PIN. Its reason
      // does not survive the trip back (it arrives as a bare "Failed to send
      // request."), so point at the requirements in the cardholder's language.
      setError(`${PAGE}-rejected`);
      setSubmitting(false);
    }
  };

  // Both ends of the Galileo round trip are terminal, and so is a PIN set
  // here: the cardholder is told what happened before moving on, because the
  // hand-off to the dashboard is a full page load that router state would not
  // survive.
  if (succeeded || commitFailed) {
    return (
      <AuthLayout
        page={PAGE}
        heading={intl.messages[`${PAGE}-heading`]}
        success={succeeded ? `${PAGE}-success` : null}
        error={commitFailed ? `${PAGE}-failed` : null}
      >
        <AuthNonForm
          page={PAGE}
          loading={false}
          buttonText={intl.messages[`${PAGE}-continue-button`]}
          onClick={finish}
        >
          <Text
            text={
              succeeded
                ? intl.messages[`${PAGE}-success-text`]
                : intl.messages[`${PAGE}-failed-text`]
            }
            textFor={succeeded ? 'success' : 'failed'}
            page={PAGE}
          />
        </AuthNonForm>
      </AuthLayout>
    );
  }

  if (loading) {
    return (
      <AuthLayout page={PAGE} heading={intl.messages[`${PAGE}-heading`]}>
        <Loader />
      </AuthLayout>
    );
  }

  const isGalileo = isGalileoProcessor(processor);

  return (
    <AuthLayout
      page={PAGE}
      heading={intl.messages[`${PAGE}-heading`]}
      subHeaderOne={
        isGalileo
          ? intl.messages[`${PAGE}-galileo-subheader`]
          : intl.messages[`${PAGE}-subheader`]
      }
      // Galileo's hosted form applies and explains its own rules.
      subHeaderTwo={isGalileo ? null : intl.messages[`${PAGE}-requirements`]}
      error={error}
    >
      <AuthForm
        page={PAGE}
        loading={submitting}
        onSubmit={e => {
          e.preventDefault();
          if (isGalileo) {
            startGalileoSetPin();
          } else {
            submitPin();
          }
        }}
        secondaryButtonText={intl.messages[`${PAGE}-skip-button`]}
        secondaryButtonFunc={finish}
      >
        {!isGalileo && (
          <>
            <FormGroup>
              <InputGroup
                label={intl.messages[`${PAGE}-new-label`]}
                labelFor="new-pin"
                page={PAGE}
              >
                <Input
                  className={fieldError ? 'is-invalid' : ''}
                  value={pin}
                  onChange={e => setPin(toPinEntry(e.target.value))}
                  data-testid="pin"
                  name="pin"
                  type="password"
                  maxLength={PIN_LENGTH}
                  inputMode="numeric"
                />
              </InputGroup>
            </FormGroup>
            <FormGroup>
              <InputGroup
                label={intl.messages[`${PAGE}-confirm-label`]}
                labelFor="confirm-pin"
                page={PAGE}
              >
                <Input
                  className={fieldError ? 'is-invalid' : ''}
                  value={confirmPin}
                  onChange={e => setConfirmPin(toPinEntry(e.target.value))}
                  data-testid="confirm-pin"
                  name="confirm_pin"
                  type="password"
                  maxLength={PIN_LENGTH}
                  inputMode="numeric"
                />
              </InputGroup>
              {fieldError && (
                <FieldError className="invalid-feedback">
                  {intl.messages[fieldError]}
                </FieldError>
              )}
            </FormGroup>
          </>
        )}
      </AuthForm>
    </AuthLayout>
  );
};
